import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

// Vinculación permanente concepto de compra ↔ punto de entrega PEMEX (el precio cambia según la semana).
export function PemexVinculoDialog({ planta, structure, it, onClose }) {
  const qc = useQueryClient();
  const actual = it.pemex?.punto || null;
  const [punto, setPunto] = useState(actual);
  const [busy, setBusy] = useState(false);
  const { data: puntos, isLoading } = useQuery({
    queryKey: ["pemex", "puntos"],
    queryFn: async () => (await api.get("/control-semanal/compra/pemex-puntos")).data.items,
  });
  const guardar = async (p) => {
    setBusy(true);
    try {
      await api.put("/control-semanal/compra/pemex-vinculo", { planta, structure, concepto_id: it.concepto_id, punto: p });
      toast.success(p ? `${it.concepto} vinculado con PEMEX · ${p}. Guarda los cambios para registrar los precios.` : `${it.concepto} desvinculado de PEMEX.`);
      qc.invalidateQueries({ queryKey: ["compra", "config", planta] });
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar la vinculación.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-white text-slate-900" data-testid="pemex-vinculo-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Configuración de precio PEMEX</DialogTitle>
          <DialogDescription className="text-slate-600">{planta} · Proveedor {it.proveedor} · Concepto {it.concepto}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p data-testid="pemex-vinculo-actual">Vinculado actualmente: <b>{actual || "Sin vinculación (captura manual)"}</b></p>
          {isLoading ? <Loader2 className="w-5 h-5 text-red-600 animate-spin" /> : (
            <Select value={punto || ""} onValueChange={setPunto}>
              <SelectTrigger className="w-full bg-slate-50 border-slate-200" data-testid="pemex-vinculo-select"><SelectValue placeholder="Selecciona el punto de entrega PEMEX" /></SelectTrigger>
              <SelectContent className="max-h-72">{(puntos || []).map((p) => <SelectItem key={p} value={p} data-testid={`pemex-vinculo-op-${p}`}>{p}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <p className="text-xs text-slate-500">La vinculación es permanente. Cada semana toma Precio sin condiciones, Ajuste por calidad y Precio sin IVA de la circular PEMEX vigente; el IVA y el porcentaje siguen siendo editables.</p>
        </div>
        <DialogFooter className="gap-2">
          {actual && <button onClick={() => guardar(null)} disabled={busy} data-testid="pemex-vinculo-quitar" className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-2"><Unlink className="w-4 h-4" />Desvincular</button>}
          <button onClick={() => guardar(punto)} disabled={busy || !punto || punto === actual} data-testid="pemex-vinculo-guardar" className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}Guardar vinculación
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
